import { useState, type ChangeEvent } from 'react';
import { Box, IconButton, TextField, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { QueryRule } from '../../../../../types/QueryBuilder';
import { useLanguage } from '../../../../../context/LanguageContext';
import FieldLabel from './FieldLabel';
import { SentenceSelect, SentenceText } from './SentenceParts';
import { DEFAULT_PURCHASE_MONTHS, lastMonthsRange, parseMonthsFromRange } from './purchasePeriod';
import { CLEARED_RULE, PairedRowProps } from './rowProps';

type Activity = 'active' | 'inactive';

const ACTIVITIES: Activity[] = ['active', 'inactive'];

const PURCHASE_COUNT_RULE: Record<Activity, Partial<QueryRule>> = {
    active: { operator: '>=', value: 1 },
    inactive: { operator: '=', value: 0 },
};

function PurchaseActivityRow({ field, rule, pairedField, pairedRule, onUpdateField }: PairedRowProps) {
    const { t } = useLanguage();
    const isApplied = rule?.operator === 'between' && typeof rule.value === 'string' && rule.value.includes('|');
    const isInactive = pairedRule?.operator === '=' && Number(pairedRule.value) === 0;

    const [activity, setActivity] = useState<Activity | ''>(isApplied ? (isInactive ? 'inactive' : 'active') : '');
    const [months, setMonths] = useState(() => (isApplied ? String(parseMonthsFromRange(rule?.value) ?? '') : ''));

    const apply = (nextActivity: Activity, nextMonths: number) => {
        setActivity(nextActivity);
        setMonths(String(nextMonths));
        onUpdateField(field.name, { operator: 'between', value: lastMonthsRange(nextMonths) });
        onUpdateField(pairedField.name, PURCHASE_COUNT_RULE[nextActivity]);
    };

    const clear = () => {
        setActivity('');
        setMonths('');
        onUpdateField(field.name, CLEARED_RULE);
        onUpdateField(pairedField.name, CLEARED_RULE);
    };

    const handleActivityChange = (value: Activity | '') => {
        if (!value) return clear();
        const currentMonths = parseInt(months, 10);
        apply(value, currentMonths > 0 ? currentMonths : DEFAULT_PURCHASE_MONTHS);
    };

    const handleMonthsChange = (event: ChangeEvent<HTMLInputElement>) => {
        const raw = event.target.value;
        if (raw === '') return clear();
        apply(activity || 'active', Math.max(1, parseInt(raw, 10) || 1));
    };

    return (
        <>
            <FieldLabel field={field} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', width: '100%' }}>
                <SentenceText>{t('pages.clients.queryBuilder.simple.quickPeriod.prefix')}</SentenceText>
                <SentenceSelect
                    value={activity}
                    options={ACTIVITIES.map(value => ({ value, label: t(`pages.clients.queryBuilder.simple.purchaseActivity.${value}`) }))}
                    minWidth={130}
                    onChange={handleActivityChange}
                />
                <TextField
                    type="number"
                    size="small"
                    value={months}
                    onChange={handleMonthsChange}
                    placeholder="—"
                    inputProps={{ min: 1 }}
                    sx={{ width: 72 }}
                />
                <SentenceText>{t('pages.clients.queryBuilder.simple.quickPeriod.suffix')}</SentenceText>
                {isApplied && (
                    <Tooltip title={t('pages.clients.queryBuilder.simple.quickPeriod.clearTooltip')} arrow>
                        <IconButton size="small" onClick={clear} sx={{ border: '1px solid', borderColor: 'divider' }}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
        </>
    );
}

export default PurchaseActivityRow;
